import { useEffect, useState } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { API, api, errMsg, money, specValue } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSpecSchema } from "@/context/SpecSchemaContext";
import { CategoryPicker } from "@/components/CategoryPicker";
import DashboardShell from "@/components/DashboardShell";
import { MandatoryTag, StatusChip, StatusLegend, AvailabilityChip } from "@/components/StatusChip";

export const TERM_LABELS = {
  advance_100: "100% advance payment",
  advance_50_50: "50% advance / 50% on delivery",
  advance_30_70: "30% advance / 70% on delivery",
  advance_30_60_10: "30% advance / 60% on shipment / 10% on delivery",
  on_delivery: "100% on delivery",
  net_30: "Net 30 days from invoice",
  net_45: "Net 45 days from invoice",
  net_60: "Net 60 days from invoice",
  lc: "Letter of Credit (LC) at sight",
  custom: "Custom terms",
};
import { Amount, Fx, FxNote } from "@/components/Currency";
import { Btn, Card, Empty, Field, Input, Loading, Modal, Pill, Select, StatCard, Table, Textarea, STATUS_TONE } from "@/components/Ui";
import { EMIRATES, UAE, licenceLabel } from "@/lib/api";
import { CountrySelect } from "@/components/CountrySelect";
import { BuyerOrders, OrderTracking } from "@/pages/Orders";
import Messages from "@/pages/Messages";
import PurchaseHistory from "@/pages/PurchaseHistory";

const NAV = [
  { to: "/buyer", label: "Dashboard", end: true, testId: "buyer-nav-dashboard" },
  { to: "/buyer/rfqs", label: "My RFQs", testId: "buyer-nav-rfqs" },
  { to: "/buyer/quotes", label: "Quotes Received", testId: "buyer-nav-quotes" },
  { to: "/buyer/saved", label: "Saved Items", testId: "buyer-nav-saved" },
  { to: "/buyer/messages", label: "Messages", testId: "buyer-nav-messages" },
  { to: "/buyer/orders", label: "Orders / Tracking", testId: "buyer-nav-orders" },
  { to: "/buyer/purchase-history", label: "Purchase History", testId: "buyer-nav-history" },
  { to: "/buyer/awards", label: "Awards", testId: "buyer-nav-awards" },
  { to: "/buyer/documents", label: "Documents", testId: "buyer-nav-documents" },
  { to: "/buyer/profile", label: "Company Profile", testId: "buyer-nav-profile" },
];

function Overview() {
  const [stats, setStats] = useState(null);
  const [rfqs, setRfqs] = useState([]);

  useEffect(() => {
    api.get("/buyer/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/buyer/rfqs").then((r) => setRfqs(r.data.slice(0, 5))).catch(() => {});
    // Mount-only dashboard fetch; `api` is a module singleton, setters are stable.
  }, []);

  return (
    <div data-testid="buyer-overview">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="stat-active-rfqs" label="Active RFQs" value={stats?.active_rfqs} />
        <StatCard testId="stat-total-rfqs" label="Total RFQs" value={stats?.total_rfqs} />
        <StatCard testId="stat-quotes" label="Quotes received" value={stats?.quotes_received} />
        <StatCard testId="stat-awards" label="Awards" value={stats?.awarded} />
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/rfq"><Btn variant="gold" data-testid="buyer-create-rfq-btn">Create an RFQ</Btn></Link>
        <Link to="/search"><Btn variant="ghost" data-testid="buyer-search-btn">Search the catalogue</Btn></Link>
      </div>
      <h2 className="mt-8 font-display text-lg font-bold tracking-tight text-[#102A43]">Recent RFQs</h2>
      {rfqs.length === 0 ? (
        <Empty testId="buyer-no-rfqs" title="No RFQs raised yet"
          sub="Search for a part, then request a quotation — the specification prefills automatically."
          action={<Link to="/rfq"><Btn>Create your first RFQ</Btn></Link>} />
      ) : (
        <Table head={["RFQ", "Title", "Qty", "Quotes", "Status", ""]} testId="buyer-recent-rfqs">
          {rfqs.map((r) => (
            <tr key={r.id} data-testid={`buyer-rfq-row-${r.rfq_number}`}>
              <td className="font-semibold text-[#102A43]">{r.rfq_number}</td>
              <td>{r.title}</td>
              <td>{r.quantity} {r.unit}</td>
              <td>{r.quote_count}</td>
              <td><Pill tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill></td>
              <td>
                <Link to={`/buyer/rfqs/${r.id}/compare`} className="text-xs font-bold text-[#147D78] underline">
                  Compare quotes
                </Link>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

function MyRfqs() {
  const [rfqs, setRfqs] = useState(null);

  const load = () => api.get("/buyer/rfqs").then((r) => setRfqs(r.data)).catch((e) => {
    toast.error(errMsg(e));
    setRfqs([]);
  });
  useEffect(() => { load(); }, []);

  const close = async (id) => {
    try {
      await api.put(`/buyer/rfqs/${id}/close`);
      toast.success("RFQ closed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!rfqs) return <Loading />;
  if (!rfqs.length)
    return <Empty testId="buyer-rfqs-empty" title="No RFQs yet" sub="Create your first request for quotation."
      action={<Link to="/rfq"><Btn>Create RFQ</Btn></Link>} />;

  return (
    <div data-testid="buyer-rfqs">
      <div className="flex justify-end">
        <Link to="/rfq"><Btn variant="gold" data-testid="buyer-new-rfq-btn">New RFQ</Btn></Link>
      </div>
      <div className="mt-4 space-y-4">
        {rfqs.map((r) => (
          <Card key={r.id} data-testid={`rfq-card-${r.rfq_number}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-display text-lg font-black tracking-tight text-[#102A43]">{r.rfq_number}</p>
                  <Pill tone={STATUS_TONE[r.status] || "neutral"} testId={`rfq-status-${r.rfq_number}`}>{r.status}</Pill>
                </div>
                <p className="text-sm text-[#334e68]">{r.title}</p>
                <p className="mt-1 text-xs text-[#8598ac]">
                  {r.product_summary || "Manual specification"} · {r.brand_requirement === "exact" ? "Exact brand required" : "Equivalent accepted"}
                  {r.acceptable_products?.length > 0 && (
                    <span data-testid={`rfq-alt-count-${r.rfq_number}`}>
                      {" "}· +{r.acceptable_products.length} acceptable equivalent
                      {r.acceptable_products.length === 1 ? "" : "s"}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to={`/buyer/rfqs/${r.id}/compare`}>
                  <Btn data-testid={`compare-quotes-btn-${r.rfq_number}`} className="px-4 py-2.5 text-xs">
                    Compare quotes ({r.quote_count})
                  </Btn>
                </Link>
                {r.status === "open" && (
                  <Btn variant="ghost" data-testid={`close-rfq-btn-${r.rfq_number}`} onClick={() => close(r.id)}
                    className="px-4 py-2.5 text-xs">
                    Close
                  </Btn>
                )}
              </div>
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-[#DFE6ED] pt-4 sm:grid-cols-4">
              {[["Quantity", `${r.quantity} ${r.unit}`],
                ["Delivery date", r.required_delivery_date || "Not specified"],
                ["Location", r.delivery_location],
                ["Incoterm", r.incoterm || "—"],
                ["Suppliers matched", r.matched_count],
                ["Certificates", (r.required_certificates || []).join(", ") || "—"],
                ["Closing date", r.closing_date || "—"],
                ["Submitted", (r.created_at || "").slice(0, 10)]].map(([l, v]) => (
                <div key={l}>
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">{l}</dt>
                  <dd className="text-sm font-medium text-[#18232F]">{v}</dd>
                </div>
              ))}
            </dl>
            {r.attachments?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {r.attachments.map((a) => (
                  <a key={a.file_id} href={`${API}/files/${a.file_id}`} target="_blank" rel="noreferrer"
                    className="rounded-full border border-[#DFE6ED] px-3 py-1.5 text-xs font-semibold text-[#102A43]">
                    {a.filename}
                  </a>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

function QuotesReceived() {
  const [quotes, setQuotes] = useState(null);
  useEffect(() => {
    api.get("/buyer/quotes").then((r) => setQuotes(r.data)).catch(() => setQuotes([]));
  }, []);
  if (!quotes) return <Loading />;
  if (!quotes.length)
    return <Empty testId="buyer-quotes-empty" title="No quotations received yet"
      sub="Matched suppliers are notified automatically when you raise an RFQ." />;

  return (
    <div data-testid="buyer-quotes">
      <Table head={["RFQ", "Supplier", "Offered", "Unit price", "Landed cost (est.)", "Availability", "Lead time", "Compliance", ""]}
        testId="buyer-quotes-table">
        {quotes.map((q) => (
          <tr key={q.id} data-testid={`quote-row-${q.id}`}>
            <td className="font-semibold text-[#102A43]">{q.rfq_number}</td>
            <td>{q.supplier_company_name}</td>
            <td>{q.offered_manufacturer} {q.part_number}</td>
            <td><Amount value={q.unit_price} currency={q.currency} /></td>
            <td className="font-semibold"><Amount value={q.landed_cost?.total_landed_cost} currency={q.currency} block /></td>
            <td><AvailabilityChip quote={q} testId={`quote-availability-${q.id}`} /></td>
            <td>{q.lead_time_days != null ? `${q.lead_time_days} days` : "—"}</td>
            <td><StatusChip status={q.technical_compliance === "meets" ? "meets" : "deviation"} compact /></td>
            <td>
              <Link to={`/buyer/rfqs/${q.rfq_id}/compare`} className="text-xs font-bold text-[#147D78] underline">
                Compare
              </Link>
            </td>
          </tr>
        ))}
      </Table>
      <p className="mt-3 text-xs text-[#8598ac]">
        Landed costs are estimates and must be confirmed before commitment.
      </p>
    </div>
  );
}

function Comparison() {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [assumptions, setAssumptions] = useState({ duty_pct: "", freight: "", clearance: "", local_delivery: "", other_charges: "" });
  const [awardFor, setAwardFor] = useState(null);
  const [terms, setTerms] = useState("");
  const [route, setRoute] = useState("direct");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState("");
  const [messaging, setMessaging] = useState("");

  const messageSupplier = async (quote) => {
    setMessaging(quote.id);
    try {
      await api.post("/messages/threads", {
        rfq_id: rfqId, counterparty_company_id: quote.supplier_company_id,
      });
      toast.success(`Conversation opened with ${quote.supplier_company_name}`);
      navigate("/buyer/messages");
    } catch (e) { toast.error(errMsg(e)); } finally { setMessaging(""); }
  };

  const download = async (format) => {
    setExporting(format);
    try {
      const body = Object.fromEntries(Object.entries(assumptions)
        .filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)]));
      const res = await api.post(`/buyer/rfqs/${rfqId}/comparison/export?format=${format}`, body,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data?.rfq?.rfq_number || "comparison"}-quote-comparison.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} approval pack downloaded`);
    } catch (e) {
      toast.error("Could not build the export pack");
    } finally {
      setExporting("");
    }
  };

  const load = async (a = {}) => {
    try {
      const body = Object.fromEntries(Object.entries(a).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.post(`/buyer/rfqs/${rfqId}/comparison`, body);
      setData(data);
    } catch (e) {
      toast.error(errMsg(e));
      setData({ rows: [] });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rfqId]);

  const award = async () => {
    if (!terms) return toast.error("Choose the payment terms for this award");
    setBusy(true);
    try {
      await api.post(`/buyer/rfqs/${rfqId}/award`,
        { quote_id: awardFor.quote.id, route, payment_terms: terms, notes: notes || null });
      toast.success(route === "managed"
        ? "Managed procurement request submitted to ADIT for review"
        : "Award recorded. The supplier has been notified.");
      setAwardFor(null);
      navigate("/buyer/awards");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Loading label="Building comparison…" />;
  const { rfq, rows, highlights, parameters } = data;

  return (
    <div data-testid="quote-comparison-page">
      <Link to="/buyer/rfqs" className="text-xs font-semibold text-[#667788] hover:text-[#102A43]">
        ← Back to my RFQs
      </Link>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-display text-2xl font-black tracking-tight text-[#102A43]">
            {rfq?.rfq_number}
          </p>
          <p className="text-sm text-[#667788]">{rfq?.title}</p>
        </div>
        <StatusLegend />
      </div>
      {rows.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-3" data-testid="comparison-export-bar">
          <Btn variant="ghost" data-testid="export-pdf-btn" loading={exporting === "pdf"}
            onClick={() => download("pdf")} className="px-5 py-2.5 text-xs">
            Download PDF pack
          </Btn>
          <Btn variant="ghost" data-testid="export-xlsx-btn" loading={exporting === "xlsx"}
            onClick={() => download("xlsx")} className="px-5 py-2.5 text-xs">
            Download Excel pack
          </Btn>
        </div>
      )}

      <Card className="mt-6">
        <h2 className="font-display text-base font-bold tracking-tight text-[#102A43]">
          Landed cost assumptions
        </h2>
        <p className="mt-1 text-sm text-[#667788]">
          Override the supplier figures to compare offers on the same basis. All results are estimates.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-5">
          {[["duty_pct", "Duty %"], ["freight", "Freight"], ["clearance", "Clearance"],
            ["local_delivery", "Local delivery"], ["other_charges", "Other charges"]].map(([k, l]) => (
            <Field key={k} label={l}>
              <Input data-testid={`assumption-${k}`} type="number" step="0.01" value={assumptions[k]}
                onChange={(e) => setAssumptions({ ...assumptions, [k]: e.target.value })} />
            </Field>
          ))}
        </div>
        <Btn data-testid="apply-assumptions-btn" onClick={() => load(assumptions)} className="mt-4">
          Recalculate landed cost
        </Btn>
      </Card>

      {rows.length === 0 ? (
        <Empty testId="comparison-empty" title="No quotations yet"
          sub="Matched suppliers have been notified. Quotations appear here as they are submitted." />
      ) : (
        <>
          <div className="mt-6 overflow-x-auto rounded-[14px] border border-[#DFE6ED] bg-white">
            <table className="spec-table w-full text-sm" data-testid="quote-comparison-table">
              <thead>
                <tr className="bg-[#102A43] text-white">
                  <th className="text-[10px] uppercase tracking-[0.12em]">Line</th>
                  {rows.map((r) => (
                    <th key={r.quote.id} className="min-w-[210px] text-[10px] uppercase tracking-[0.12em]">
                      {r.quote.supplier_company_name}
                      <br />
                      <span className="font-normal normal-case tracking-normal text-[#bcccdc]">
                        {r.quote.offered_manufacturer} {r.quote.part_number}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["Seller", (r) => r.quote.supplier_company_name],
                  ["Offered manufacturer", (r) => r.quote.offered_manufacturer],
                  ["Part number", (r) => r.quote.part_number],
                  ["Country of origin", (r) => r.quote.country_of_origin || "—"],
                  ["Availability", (r) => <AvailabilityChip quote={r.quote} />],
                  ["Stock located in", (r) => (r.quote.availability === "to_order" ? "Not held in stock"
                    : [r.quote.stock_location, r.quote.stock_country].filter(Boolean).join(", ") || "—")],
                  ["Quantity", (r) => r.quote.quantity],
                  ["Unit price", (r) => <Amount value={r.quote.unit_price} currency={r.quote.currency} block />],
                  ["Product total", (r) => <Amount value={r.quote.landed_cost?.product_total} currency={r.quote.currency} block />],
                  ["Freight", (r) => <Amount value={r.quote.landed_cost?.freight} currency={r.quote.currency} block />],
                  ["Estimated duty", (r) => <><Amount value={r.quote.landed_cost?.duty} currency={r.quote.currency} block /> ({r.quote.landed_cost?.duty_pct}%)</>],
                  ["Clearance / handling", (r) => <Amount value={r.quote.landed_cost?.clearance} currency={r.quote.currency} block />],
                  ["Local delivery / other", (r) => <Amount value={(r.quote.landed_cost?.local_delivery || 0) + (r.quote.landed_cost?.other_charges || 0)} currency={r.quote.currency} block />],
                  ["Lead time", (r) => (r.quote.lead_time_days != null ? `${r.quote.lead_time_days} days` : "—")],
                  ["Incoterm", (r) => r.quote.incoterm || "—"],
                  ["Warranty", (r) => (r.quote.warranty_months ? `${r.quote.warranty_months} months` : "—")],
                  ["Payment terms", (r) => r.quote.payment_terms_summary || r.quote.payment_terms || "—"],
                  ["Quote validity", (r) => (r.quote.validity_days ? `${r.quote.validity_days} days` : "—")],
                  ["Certificates", (r) => r.quote.certificate_references || "—"],
                  ["Declared deviations", (r) => r.quote.technical_deviations || "None declared"],
                ].map(([label, fn]) => (
                  <tr key={label} className="hover:bg-[#f8fafc]">
                    <td className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">{label}</td>
                    {rows.map((r) => (
                      <td key={r.quote.id} data-testid={`cmp-${r.quote.id}-${String(label).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        {fn(r)}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="bg-[#F5F7FA]">
                  <td className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">
                    Total landed cost (est.)
                  </td>
                  {rows.map((r) => (
                    <td key={r.quote.id} data-testid={`landed-total-${r.quote.id}`}>
                      <span className="font-display text-base font-black text-[#102A43]">
                        {money(r.quote.landed_cost?.total_landed_cost, r.quote.currency)}
                      </span>
                      <Fx amount={r.quote.landed_cost?.total_landed_cost} from={r.quote.currency}
                        className="mt-0.5 block text-xs font-semibold" />
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {highlights.lowest_landed_cost === r.quote.id && (
                          <Pill tone="ok" testId={`highlight-lowest-${r.quote.id}`}>Lowest landed cost</Pill>
                        )}
                        {highlights.fastest_delivery === r.quote.id && (
                          <Pill tone="gold" testId={`highlight-fastest-${r.quote.id}`}>Fastest delivery</Pill>
                        )}
                        {(highlights.no_technical_deviations || []).includes(r.quote.id) && (
                          <Pill tone="ok" testId={`highlight-clean-${r.quote.id}`}>No technical deviations</Pill>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">
                    Technical conformity
                  </td>
                  {rows.map((r) => (
                    <td key={r.quote.id}>
                      <StatusChip status={r.verdict} testId={`quote-verdict-${r.quote.id}`} />
                      <p className="mt-1 text-[10px] text-[#8598ac]">
                        {r.spec_source === "catalogue" ? "Catalogue-verified spec" : "Supplier declaration"}
                      </p>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">Documents</td>
                  {rows.map((r) => (
                    <td key={r.quote.id}>
                      {(r.quote.documents || []).length === 0 ? "—" : (
                        <div className="flex flex-col gap-1">
                          {r.quote.documents.map((d) => (
                            <a key={d.file_id} href={`${API}/files/${d.file_id}`} target="_blank" rel="noreferrer"
                              className="text-xs font-semibold text-[#147D78] underline">
                              {d.filename}
                            </a>
                          ))}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
                {parameters?.length > 0 && rows.some((r) => r.parameters) && (
                  <>
                    <tr className="bg-[#102A43] text-white">
                      <td colSpan={rows.length + 1} className="text-[10px] font-semibold uppercase tracking-[0.14em]">
                        Parameter-by-parameter conformity
                      </td>
                    </tr>
                    {parameters.map((p) => (
                      <tr key={p.key}>
                        <td className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">
                          {p.label}
                          <MandatoryTag mandatory={p.mandatory} />
                        </td>
                        {rows.map((r) => {
                          const row = r.parameters?.find((x) => x.key === p.key);
                          return (
                            <td key={r.quote.id}>
                              {row ? (
                                <>
                                  <div className="text-sm">{row.offered}</div>
                                  <div className="mt-1"><StatusChip status={row.status} compact /></div>
                                </>
                              ) : "Not mapped"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </>
                )}
                <tr className="bg-[#F5F7FA]">
                  <td />
                  {rows.map((r) => (
                    <td key={r.quote.id}>
                      <div className="flex flex-col gap-2">
                        {rfq?.status === "awarded" ? (
                          <Pill tone={r.quote.status === "won" ? "ok" : "neutral"}>{r.quote.status}</Pill>
                        ) : (
                          <Btn data-testid={`select-offer-btn-${r.quote.id}`} onClick={() => setAwardFor(r)}
                            className="px-4 py-2.5 text-xs">
                            Select this offer
                          </Btn>
                        )}
                        <Btn variant="ghost" className="px-4 py-2 text-xs"
                          data-testid={`message-supplier-btn-${r.quote.id}`}
                          loading={messaging === r.quote.id}
                          onClick={() => messageSupplier(r.quote)}>
                          Message seller
                        </Btn>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-[#8598ac]" data-testid="comparison-disclaimer">
            {data.disclaimer} ADIT Connect does not recommend a supplier — the award decision is yours.
          </p>
        </>
      )}

      {awardFor && (
        <Modal title="Choose your procurement route" onClose={() => setAwardFor(null)}>
          <p className="text-sm text-[#334e68]">
            Selected offer: <strong>{awardFor.quote.supplier_company_name}</strong> —{" "}
            {awardFor.quote.offered_manufacturer} {awardFor.quote.part_number}, estimated landed cost{" "}
            {money(awardFor.quote.landed_cost?.total_landed_cost, awardFor.quote.currency)}
            <Fx amount={awardFor.quote.landed_cost?.total_landed_cost} from={awardFor.quote.currency}
              className="ml-1 text-xs" />.
          </p>
          <div className="mt-5 space-y-3">
            {[["direct", "Direct purchase",
              "You purchase directly from the selected supplier. That supplier is responsible for the product, technical conformity, delivery, documentation, warranty, returns and all seller obligations. ADIT Connect acts as the sourcing and specification platform."],
              ["managed", "Managed by ADIT",
                "You request ADIT Trading to handle this procurement. A Managed Procurement Request is raised for ADIT review. ADIT does not become the seller until it reviews and accepts this specific request."]]
              .map(([v, t, d]) => (
              <button key={v} type="button" data-testid={`route-option-${v}`} onClick={() => setRoute(v)}
                className={`w-full rounded-[9px] border p-4 text-left transition-colors ${
                  route === v ? "border-[#F59E0B] bg-[#F59E0B]/10" : "border-[#DFE6ED] bg-white"
                }`}>
                <p className="font-display text-sm font-bold text-[#102A43]">{t}</p>
                <p className="mt-1.5 text-xs leading-relaxed text-[#667788]">{d}</p>
              </button>
            ))}
          </div>
          <div className="mt-6">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#147D78]">
              Payment terms offered by this supplier
            </p>
            <div className="mt-3 space-y-2" data-testid="award-payment-terms">
              {(awardFor.quote.payment_terms_options || []).length === 0 && (
                <p className="text-xs text-[#667788]">
                  This supplier quoted before structured terms existed: {awardFor.quote.payment_terms || "not stated"}.
                </p>
              )}
              {(awardFor.quote.payment_terms_options || []).map((k) => (
                <button key={k} type="button" data-testid={`payment-term-${k}`} onClick={() => setTerms(k)}
                  className={`flex w-full items-center gap-3 rounded-[9px] border p-3 text-left text-sm transition-colors ${
                    terms === k ? "border-[#F59E0B] bg-[#F59E0B]/10" : "border-[#DFE6ED] bg-white"
                  }`}>
                  <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border ${
                    terms === k ? "border-[#F59E0B]" : "border-[#8598ac]"}`}>
                    {terms === k && <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />}
                  </span>
                  <span className="font-semibold text-[#102A43]">
                    {TERM_LABELS[k] || k}
                    {k === "custom" && awardFor.quote.payment_terms_custom
                      ? ` — ${awardFor.quote.payment_terms_custom}` : ""}
                  </span>
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-[#8598ac]">
              Payment is settled directly between you and the supplier — ADIT records the agreed terms
              on the order and its documents.
            </p>
          </div>
          <Field label="Notes for this award" className="mt-4">
            <Textarea data-testid="award-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <Btn data-testid="confirm-award-btn" loading={busy} onClick={award} className="mt-5 w-full">
            {route === "managed" ? "Submit managed procurement request" : "Confirm direct award"}
          </Btn>
        </Modal>
      )}
    </div>
  );
}

function Saved() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/saved").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  if (!items) return <Loading />;
  const groups = [["product", "Saved Products"], ["crossref", "Saved Comparisons"]];
  return (
    <div data-testid="buyer-saved" className="space-y-8">
      {groups.map(([type, title]) => {
        const list = items.filter((i) => i.type === type);
        return (
          <div key={type}>
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">{title}</h2>
            {list.length === 0 ? (
              <Empty testId={`saved-empty-${type}`} title="Nothing saved yet"
                sub="Use the bookmark button on search results and product pages." />
            ) : (
              <Table head={["Item", "Saved", ""]}>
                {list.map((i) => (
                  <tr key={i.id} data-testid={`saved-row-${i.id}`}>
                    <td className="font-semibold text-[#102A43]">{i.label || i.ref_id}</td>
                    <td>{(i.created_at || "").slice(0, 10)}</td>
                    <td className="flex gap-3">
                      <Link to={`/products/${i.ref_id}`} className="text-xs font-bold text-[#147D78] underline">Open</Link>
                      <button data-testid={`remove-saved-${i.id}`}
                        onClick={async () => { await api.delete(`/saved/${i.id}`); load(); }}
                        className="text-xs font-bold text-[#B42318] underline">Remove</button>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Awards() {
  const [awards, setAwards] = useState(null);
  useEffect(() => {
    api.get("/buyer/awards").then((r) => setAwards(r.data)).catch(() => setAwards([]));
  }, []);
  if (!awards) return <Loading />;
  if (!awards.length)
    return <Empty testId="buyer-awards-empty" title="No awards yet"
      sub="When you select an offer you can buy directly or ask ADIT to manage the procurement." />;
  return (
    <div data-testid="buyer-awards">
      <Table head={["RFQ", "Supplier", "Part", "Route", "Landed cost (est.)", "Status"]}>
        {awards.map((a) => (
          <tr key={a.id} data-testid={`award-row-${a.rfq_number}`}>
            <td className="font-semibold text-[#102A43]">{a.rfq_number}</td>
            <td>{a.supplier_company_name}</td>
            <td>{a.part_number}</td>
            <td>{a.route === "managed" ? "Managed by ADIT" : "Direct purchase"}</td>
            <td><Amount value={a.total_landed_cost} currency={a.currency} block /></td>
            <td><Pill tone={STATUS_TONE[a.status] || "neutral"} testId={`award-status-${a.rfq_number}`}>{a.status.replace(/_/g, " ")}</Pill></td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function Documents() {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    api.get("/buyer/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, []);
  if (!docs) return <Loading />;
  if (!docs.length) return <Empty testId="buyer-docs-empty" title="No documents yet"
    sub="RFQ attachments and supplier quotation documents appear here." />;
  return (
    <div data-testid="buyer-documents">
      <Table head={["File", "Type", "Source", ""]}>
        {docs.map((d, i) => (
          <tr key={`${d.file_id}-${i}`} data-testid={`document-row-${d.file_id}`}>
            <td className="font-semibold text-[#102A43]">{d.filename}</td>
            <td>{d.kind || "other"}</td>
            <td>{d.source}</td>
            <td>
              <a href={`${API}/files/${d.file_id}`} target="_blank" rel="noreferrer"
                className="text-xs font-bold text-[#147D78] underline">Open</a>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

export function CompanyProfile({ supplier = false }) {
  const { refresh } = useAuth();
  const schema = useSpecSchema();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/company").then((r) => setC({ ...r.data,
      categories: (r.data.categories || []).join(", "),
      subcategories: (r.data.subcategories || []).join(", "),
      brands: (r.data.brands || []).join(", "),
      markets_served: (r.data.markets_served || []).join(", "),
      certifications: (r.data.certifications || []).join(", "),
    })).catch(() => setC(false));
  }, []);

  if (c === null) return <Loading />;
  if (c === false) return <Empty title="No company profile linked" />;

  const listify = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
  const save = async () => {
    if (!c.trade_licence) return toast.error(`${licenceLabel(c.country).replace(" *", "")} is required`);
    if (supplier && listify(c.categories).length === 0) {
      return toast.error("Tick at least one product category — RFQ matching depends on it");
    }
    setBusy(true);
    try {
      await api.put("/company", {
        name: c.name, country: c.country, emirate: c.emirate || null,
        state: c.state || null, city: c.city || null,
        trade_licence: c.trade_licence || null, industry: c.industry || null,
        website: c.website || null, contact_person: c.contact_person || null,
        phone: c.phone || null, supplier_type: c.supplier_type || null,
        categories: listify(c.categories), subcategories: listify(c.subcategories),
        brands: listify(c.brands), markets_served: listify(c.markets_served),
        certifications: listify(c.certifications), documents: c.documents || [],
      });
      await refresh();
      toast.success("Company profile updated");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });

  return (
    <div data-testid="company-profile">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">Company profile</h2>
          <Pill tone={c.verification_status === "verified" ? "ok" : c.verification_status === "rejected" ? "bad" : "warn"}
            testId="profile-verification">
            Verification: {c.verification_status}
          </Pill>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <Field label="Company name"><Input data-testid="profile-name" value={c.name || ""} onChange={set("name")} /></Field>
          <Field label={licenceLabel(c.country)}><Input data-testid="profile-licence" required value={c.trade_licence || ""} onChange={set("trade_licence")} /></Field>
          <Field label="Country">
            <CountrySelect testId="profile-country" value={c.country || ""}
              onChange={(v) => setC({ ...c, country: v, emirate: v === UAE ? c.emirate : "" })} />
          </Field>
          {c.country === UAE ? (
            <Field label="Emirate"><Select data-testid="profile-emirate" value={c.emirate || ""} onChange={set("emirate")} options={["", ...EMIRATES]} /></Field>
          ) : (
            <Field label="State / Province / Region"><Input data-testid="profile-state" value={c.state || ""} onChange={set("state")} /></Field>
          )}
          <Field label="City"><Input data-testid="profile-city" value={c.city || ""} onChange={set("city")} /></Field>
          <Field label="Industry"><Input data-testid="profile-industry" value={c.industry || ""} onChange={set("industry")} /></Field>
          <Field label="Website"><Input data-testid="profile-website" value={c.website || ""} onChange={set("website")} /></Field>
          <Field label="Contact person"><Input data-testid="profile-contact" value={c.contact_person || ""} onChange={set("contact_person")} /></Field>
          <Field label="Phone / WhatsApp"><Input data-testid="profile-phone" value={c.phone || ""} onChange={set("phone")} /></Field>
          {supplier && (
            <>
              <div className="sm:col-span-2">
                <CategoryPicker testId="profile-categories" label="Product categories *"
                  hint="Drives RFQ matching — tick every category you can supply"
                  options={schema.published} value={listify(c.categories)}
                  onChange={(next) => setC({ ...c, categories: next.join(", ") })} />
              </div>
              <Field label="Subcategories" hint="Comma separated">
                <Input data-testid="profile-subcategories" value={c.subcategories} onChange={set("subcategories")} />
              </Field>
              <Field label="Brands" hint="Comma separated">
                <Input data-testid="profile-brands" value={c.brands} onChange={set("brands")} />
              </Field>
              <Field label="Markets served" hint="Comma separated">
                <Input data-testid="profile-markets" value={c.markets_served} onChange={set("markets_served")} />
              </Field>
              <Field label="Certifications" className="sm:col-span-2" hint="Comma separated">
                <Textarea rows={2} data-testid="profile-certifications" value={c.certifications} onChange={set("certifications")} />
              </Field>
            </>
          )}
        </div>
        <Btn data-testid="save-profile-btn" loading={busy} onClick={save} className="mt-6">Save profile</Btn>
      </Card>
    </div>
  );
}

export default function BuyerPortal() {
  return (
    <DashboardShell title="Buyer dashboard" subtitle="Procurement workspace" nav={NAV} testId="buyer-dashboard">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="rfqs" element={<MyRfqs />} />
        <Route path="rfqs/:rfqId/compare" element={<Comparison />} />
        <Route path="quotes" element={<QuotesReceived />} />
        <Route path="saved" element={<Saved />} />
        <Route path="awards" element={<Awards />} />
        <Route path="orders" element={<BuyerOrders />} />
        <Route path="purchase-history" element={<PurchaseHistory />} />
        <Route path="messages" element={<Messages role="buyer" />} />
        <Route path="orders/:orderId" element={<OrderTracking />} />
        <Route path="documents" element={<Documents />} />
        <Route path="profile" element={<CompanyProfile />} />
      </Routes>
    </DashboardShell>
  );
}
